/**
 * Auth Store - 認證狀態管理
 * 
 * Manages authentication state using Pinia store:
 * - isAuthenticated: 是否已登入
 * - username: 使用者名稱
 * - authentication: 權限等級
 * - accessToken: JWT Token
 * 
 * Provides computed properties for permission checks:
 * - isAdmin: 是否為管理員 (admin 或 engineer)
 * - canEdit: 是否可編輯 (editor, admin, 或 engineer)
 * 
 * Requirements: 12.4, 3.6
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { AuthenticationLevel } from '@/types/user';
import {
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  confirmSignInWithNewPassword as cognitoConfirmNewPassword,
  getUserSession,
  configureAuth,
  refreshToken as cognitoRefreshToken,
  type UserSession,
} from '@/lib/auth';
import { setTokenProvider } from '@/lib/api';

/**
 * Login result type
 */
export type LoginResult = 
  | { success: true }
  | { success: false; requiresNewPassword: true }
  | { success: false; requiresNewPassword: false };

export const useAuthStore = defineStore('auth', () => {
  // State
  const isAuthenticated = ref(false);
  const username = ref<string | null>(null);
  const authentication = ref<AuthenticationLevel>('watcher');
  const accessToken = ref<string | null>(null);
  const idToken = ref<string | null>(null);
  const email = ref<string | null>(null);
  const phone = ref<string | null>(null);
  const lastName = ref<string | null>(null);
  const firstName = ref<string | null>(null);
  const isInitialized = ref(false);
  const isLoading = ref(false);

  // Computed properties for permission checks
  /**
   * Check if user is admin (admin or engineer)
   * 
   * Requirements: 3.6
   */
  const isAdmin = computed(() => 
    authentication.value === 'admin' || authentication.value === 'engineer'
  );

  /**
   * Check if user can edit (editor, admin, or engineer)
   * 
   * Requirements: 3.6
   */
  const canEdit = computed(() => 
    ['editor', 'admin', 'engineer'].includes(authentication.value)
  );

  /**
   * Get user's display name
   * Priority: 姓名 > email > username
   */
  const displayName = computed(() => {
    if (firstName.value && lastName.value) {
      return `${lastName.value}${firstName.value}`;
    }
    if (lastName.value) {
      return lastName.value;
    }
    if (firstName.value) {
      return firstName.value;
    }
    // Use email if no name is set
    if (email.value) {
      return email.value;
    }
    return username.value || '';
  });

  // Actions
  /**
   * Initialize the auth store
   * 
   * Sets up the token provider for the API client and
   * attempts to restore the session from Cognito.
   */
  async function initialize(): Promise<void> {
    if (isInitialized.value) return;

    // Configure Amplify Auth
    configureAuth();

    // Set up token provider for API client
    // Use ID Token for Cognito Authorizer (COGNITO_USER_POOLS type expects ID Token)
    setTokenProvider({
      getAccessToken: () => idToken.value,
      refreshToken: async () => {
        const newToken = await cognitoRefreshToken();
        if (newToken) {
          // Refresh the full session to get updated ID token
          const session = await getUserSession();
          if (session) {
            setAuthState(session);
            return session.idToken;
          }
        }
        return null;
      },
      clearAuth: () => clearAuthState(),
    });

    // Try to restore session
    await restoreSession();
    isInitialized.value = true;
  }

  /**
   * Restore session from Cognito
   * 
   * Attempts to get the current user session from Cognito.
   * If successful, updates the store state.
   */
  async function restoreSession(): Promise<boolean> {
    try {
      isLoading.value = true;
      const session = await getUserSession();
      
      if (session) {
        setAuthState(session);
        return true;
      }
      
      return false;
    } catch {
      clearAuthState();
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Sign in with username and password
   * 
   * @param usernameInput - The user's username
   * @param password - The user's password
   * @returns LoginResult indicating success or if new password is required
   * 
   * Requirements: 2.5
   */
  async function login(usernameInput: string, password: string): Promise<LoginResult> {
    try {
      isLoading.value = true;
      
      // Sign in with Cognito
      const result = await cognitoSignIn(usernameInput, password);
      
      // Check if new password is required
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return { success: false, requiresNewPassword: true };
      }
      
      if (result.isSignedIn) {
        // Get the full session with tokens and attributes
        const session = await getUserSession();
        
        if (session) {
          setAuthState(session);
          return { success: true };
        }
      }
      
      return { success: false, requiresNewPassword: false };
    } catch (error) {
      clearAuthState();
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Complete sign in with new password
   * 
   * Called when user needs to set a new password after initial login.
   * 
   * @param newPassword - The new password to set
   * @returns true if successful
   * 
   * Requirements: 2.5
   */
  async function completeNewPassword(newPassword: string): Promise<boolean> {
    try {
      isLoading.value = true;
      
      const result = await cognitoConfirmNewPassword(newPassword);
      
      if (result.isSignedIn) {
        // Get the full session with tokens and attributes
        const session = await getUserSession();
        
        if (session) {
          setAuthState(session);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      clearAuthState();
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Sign out the current user
   * 
   * Clears the local state and signs out from Cognito.
   * 
   * Requirements: 2.5
   */
  async function logout(): Promise<void> {
    try {
      isLoading.value = true;
      await cognitoSignOut();
    } finally {
      clearAuthState();
      isLoading.value = false;
    }
  }

  /**
   * Set auth state from user session
   */
  function setAuthState(session: UserSession): void {
    isAuthenticated.value = true;
    username.value = session.username;
    authentication.value = session.authentication;
    accessToken.value = session.accessToken;
    idToken.value = session.idToken;
    email.value = session.email || null;
    phone.value = session.phone || null;
    lastName.value = session.lastName || null;
    firstName.value = session.firstName || null;
  }

  /**
   * Clear auth state
   */
  function clearAuthState(): void {
    isAuthenticated.value = false;
    username.value = null;
    authentication.value = 'watcher';
    accessToken.value = null;
    idToken.value = null;
    email.value = null;
    phone.value = null;
    lastName.value = null;
    firstName.value = null;
  }

  /**
   * Update access token
   * 
   * Used when token is refreshed.
   */
  function updateAccessToken(token: string): void {
    accessToken.value = token;
  }

  return {
    // State
    isAuthenticated,
    username,
    authentication,
    accessToken,
    email,
    phone,
    lastName,
    firstName,
    isInitialized,
    isLoading,

    // Computed
    isAdmin,
    canEdit,
    displayName,

    // Actions
    initialize,
    restoreSession,
    login,
    completeNewPassword,
    logout,
    setAuthState,
    clearAuthState,
    updateAccessToken,
  };
});
